/* eslint-disable @typescript-eslint/naming-convention */
import { Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Gtag } from 'angular-gtag';
import { AlertLevel, AlertsService } from '../../common/alerts/alerts.service';
import { AuthService, RegistrationData } from '../../auth/auth.service';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { controlValueIsSameValidator } from '../../common/validators/control-value-is-same';
import { StripeCardElementOptions, StripeElementsOptions, StripeElements, StripeCardElement, StripeCardElementChangeEvent } from '@stripe/stripe-js';
import { StripeService } from 'ngx-stripe';
import { DeckplanStripeService } from '../../users/deckplan-stripe.service';
import { environment } from '../../../environments/environment';
import { free, planner } from '../../common/pricing/products';

const PASSWORD_MIN_LENGTH = 8;

@Component({
    selector: 'app-signup',
    templateUrl: './signup.component.html',
    styleUrls: ['./signup.component.scss']
})
export class SignupComponent implements OnInit {

    public signUp: FormGroup;

    public showPlannerFields = false;

    public cardOptions: StripeCardElementOptions = {
        style: {
            base: {
                iconColor: '#666EE8',
                color: '#31325F',
                fontWeight: '300',
                fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
                fontSize: '18px',
                '::placeholder': {
                    color: '#CFD7E0'
                }
            }
        }
    };

    public elementsOptions: StripeElementsOptions = {
        locale: 'en'
    };

    public elements: StripeElements;
    public card: StripeCardElement;
    public cardError: string;
    public stripeError: string;

    public currentProduct = free;

    constructor(formBuilder: FormBuilder,
                private readonly alertsService: AlertsService,
                private readonly authService: AuthService,
                private readonly stripeService: StripeService,
                private readonly deckplanStripeService: DeckplanStripeService,
                private readonly router: Router,
                private readonly route: ActivatedRoute,
                private readonly gtag: Gtag) {
        this.signUp = formBuilder.group({
            alias: ['', Validators.required],
            email: ['', [Validators.required, Validators.email]],
            password: ['', [Validators.required, Validators.minLength(PASSWORD_MIN_LENGTH)]],
            passwordConfirm: ['', Validators.required],
            accountType: ['', Validators.required],
            fullName: ['', Validators.required],
            agreeToTerms: ['', Validators.required]
        });

        // We need to wait until the form has been constructed before we can set a validator
        // that depends on one of the other form fields; so leave the validators for this field
        // in the formBuilder simple and set the real ones here.
        this.signUp.get('passwordConfirm').setValidators([
            Validators.required,
            Validators.minLength(PASSWORD_MIN_LENGTH),
            controlValueIsSameValidator(this.signUp.get('password'))
        ]);
    }

    public ngOnInit(): void {
        this.gtag.pageview({
            page_title: 'SignUp',
            page_path: '/signup',
            page_location: 'https://deckplan.io/'
        });

        this.signUp.get('accountType').valueChanges.subscribe(radioValue => this.accountTypeChanged(radioValue === 'planner'));

        // Read out the alias and email we were given on the front page, if the user used that. Use the account type
        // info we were given, if present.
        this.route.paramMap.pipe(
            filter((params: ParamMap) => params.get('alias') !== null || params.get('isPlanner') !== null)
        ).subscribe(params => {
            if (params.get('alias') !== null) {
                this.signUp.get('alias').setValue(params.get('alias'));
                this.signUp.get('email').setValue(params.get('email'));
            }

            if (params.get('isPlanner') !== null) {
                this.signUp.get('accountType').setValue(params.get('isPlanner') === 'true' ? 'planner' : 'free');
            }
        });

        if (this.showPlannerFields) {
            this.mountCardElement();
        }
    }

    public onSubmit(): void {
        if (!this.signUp.valid) {
            this.alertsService.alert('Something was wrong in the signup form', AlertLevel.InfoFailure);
            return;
        }

        const user = {
            alias: this.signUp.controls.alias.value,
            email: this.signUp.controls.email.value,
            password: this.signUp.controls.password.value,
            planner: this.showPlannerFields,
            isAdmin: false
        };

        const errorHandler = (err: { status: number; error: { message: string } }) => {
            let error;
            if (err.status === 0) {
                error = 'Unable to reach the server';
            } else if (err.status === 401) {
                error = `You are not authorized to sign up as a user.`;
            } else if (err.status === 400) {
                if (err.error.message.includes('DUPLICATE_USER')) {
                    error = 'That email address cannot be registered, it may already be associated with an account.';
                } else {
                    error = 'Something was incorrect: ' + err.error.message;
                }
            } else {
                error = 'An unknown error has occurred, please try again later.';
            }

            this.alertsService.alert(error, AlertLevel.InfoFailure);
        };

        this.authService.register(user as any).subscribe((userData: RegistrationData) => {
            // If they've chosen free, then redirect at this point, rather than doing billing set up.
            if (!this.showPlannerFields) {
                this.successRedirect('user_registered', userData.userId);
            } else {
                this.createPaymentMethod(userData.customerId, userData.userId);
            }
        }, (err) => errorHandler(err));
    }

    public accountTypeChanged(isPlanner: boolean): void {
        this.showPlannerFields = isPlanner;
        if (isPlanner) {
            this.signUp.get('fullName').enable();
            // The element we're trying to mount to doesn't exist yet, by the time this method is called,
            // which makes Stripe angry. Therefore, set a zero-timer to run the card element mounting immediately
            // after the render loop concludes
            setTimeout(() => { this.mountCardElement(); }, 0);
            this.currentProduct = planner;
        } else {
            this.signUp.get('fullName').disable();
            this.currentProduct = free;
        }
    }

    public handleCardError(event: StripeCardElementChangeEvent): void {
        if (event.error) {
            this.cardError = event.error.message;
        } else {
            this.cardError = '';
        }
    }

    public handleStripeError(error: string): void {
        if (error) {
            this.stripeError = error;
        } else {
            this.stripeError = '';
        }
    }

    private mountCardElement(): void {
        this.stripeService.elements(this.elementsOptions)
            .subscribe(elements => {
                this.elements = elements;
                // Only create the element the first time
                if (!this.card) {
                    this.card = this.elements.create('card', this.cardOptions);
                    this.card.on('change', changeEvent => this.handleCardError(changeEvent));
                }

                // We need to mount it every time we come in here, since we're using an ngIf to control if the fields
                // are visible or not, it gets unmounted with the parent container is removed by the if.
                this.card.mount('#cardElement');
            });
    }

    private successRedirect(gtagEventName: string, userAccountIdentifier: number) {
        // If we were successful in our operation, navigate the user to the home page and then show our welcome notification
        this.gtag.event(gtagEventName, {
            account: userAccountIdentifier,
            event_category: 'account'
        });
        this.router.navigate(['verify'])
            .then((navigated: boolean) => {
                if (navigated) {
                    this.alertsService.alert('Please verify your email!', AlertLevel.InfoSuccess);
                }
            });
    }

    private createPaymentMethod(customerId: string, userId: number) {
        const billingName = this.signUp.controls.fullName.value;

        // If we're at this point, then they've selected paid, so set up the billing stuff
        this.stripeService.createPaymentMethod({
            type: 'card',
            card: this.card,
            billing_details: {
                name: billingName
            }
        }).subscribe((result) => {
            if (result.error) {
                this.handleStripeError(result.error.message);
            } else {
                this.createSubscription(customerId, result.paymentMethod.id, environment.stripePriceId, userId);
            }
        });
    }

    private createSubscription(customerId: string, paymentMethodId: string, priceId: string, userId: number): void {
        this.deckplanStripeService.createSubscription(customerId, paymentMethodId, priceId, userId)
            .subscribe((subscription: any) => {
                if (subscription && subscription.status === 'active') {
                    this.successRedirect('user_subscribed', userId);
                    return;
                }

                // If we've gotten down here, then we need to handle additional payment details (e.g. 3DSecure)
                this.handleAdditionalPaymentAction(subscription, paymentMethodId, userId);
            }, (error) => {
                this.handleStripeError(error);
            });
    }

    private handleAdditionalPaymentAction(subscription: any, paymentMethodId: string, userId: number) {
        const paymentIntent = subscription.latest_invoice.payment_intent;
        if (paymentIntent.status === 'requires_action') {
            this.stripeService.confirmCardPayment(paymentIntent.client_secret, { payment_method: paymentMethodId })
                .subscribe((result) => {
                    if (result.error) {
                        this.handleStripeError(result.error.message);
                    } else if (result.paymentIntent.status === 'succeeded') {
                        this.successRedirect('user_subscribed', userId);
                    }
                });
        } else {
            this.successRedirect('user_subscribed', userId);
        }
    }
}
